import React from "react";
import styled from "styled-components";
import { Link } from "gatsby";
import { Container, Row, Col } from "react-bootstrap";

import PageWrapper from "../components/PageWrapper";
import { useAuth, hasRole, hasTag } from "../context/AuthContext";
import CTA from "../sections/landing4/CTA";
import Seo from "../components/Seo";

const TOOLS = [
  // Public
  {
    label: "PRACTICE",
    name: "DevOps Labs",
    description:
      "40 challenges that teach DevOps the way we learned — by building something, watching it fail, and figuring out why.",
    tags: ["HANDS-ON", "8 CATEGORIES", "40 CHALLENGES"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/labs",
    minRole: "public",
  },
  {
    label: "GROWTH",
    name: "Career Path",
    description:
      "Interactive roadmap from junior to staff. Track your skills, find resources, see what's next.",
    tags: ["5 LEVELS", "30 SKILLS", "SELF-ASSESSMENT"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/career-path",
    minRole: "public",
  },
  {
    label: "MENTORSHIP",
    name: "Junior Staffing",
    description:
      "We place junior engineers in your team and mentor them. They learn your stack and become your people.",
    tags: ["EMBEDDED", "MENTORED", "HUMAN-FIRST"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/staffing",
    minRole: "public",
  },
  {
    label: "KNOWLEDGE",
    name: "Runbook Builder",
    description:
      "Incident response docs and operational runbooks. Written as we work so you're never stuck if we leave.",
    tags: ["DOCUMENTATION", "HANDOVER", "TEAM-OWNED"],
    status: "COMING SOON",
    statusColor: "var(--nd-warning)",
    href: null,
    minRole: "public",
  },
  {
    label: "VISIBILITY",
    name: "Infrastructure Dashboard",
    description:
      "One screen: every junior on every project, with the tech stacks in play. Project names obfuscated.",
    tags: ["LIVE-MAP", "DELIVERY", "SHARED-WITH-CLIENTS"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/infrastructure",
    minRole: "client",
  },
  // Staff
  {
    label: "OPERATIONS",
    name: "Time Tracker",
    description:
      "Log hours per project, submit for admin approval, download CSV. CLI ships with it.",
    tags: ["REPORTING", "PER-CLIENT", "CLI-FRIENDLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/time-tracker",
    minRole: "staff",
  },
  // Admin
  {
    label: "ADMIN · USERS",
    name: "User Management",
    description:
      "Promote, demote, audit users. Self-edit disabled. Every change writes to the audit log.",
    tags: ["ROLE FLIPS", "EMAIL LOOKUP", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/admin",
    minRole: "admin",
  },
  {
    label: "ADMIN · COMPLIANCE",
    name: "Audit Log",
    description:
      "Last 200 privileged actions — role changes, deletions, invoice creations. Immutable, admin-read.",
    tags: ["IMMUTABLE", "200 ROWS", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/admin-audit",
    minRole: "admin",
  },
  {
    label: "ADMIN · DELIVERY",
    name: "Projects",
    description:
      "Client projects and engagements. Auto-created from won leads or added manually. Assign juniors, log hours, invoice.",
    tags: ["CRUD", "JUNIOR-LINKED", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/admin-projects",
    minRole: "admin",
  },
  {
    label: "ADMIN · MENTORSHIP",
    name: "Mentor Dashboard",
    description:
      "Track juniors — progress, metrics, comments & plans",
    tags: ["PROGRESS", "AT-RISK", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/mentor-dashboard",
    minRole: "admin",
    group: "mentorship",
  },
  {
    label: "ADMIN · ACADEMY",
    name: "Learning Paths",
    description:
      "Author DevOps learning paths (labs, modules, certs) and assign one per junior. Progress auto-tracks from lab/module/cert signals; milestones award XP + a badge. Includes the manager dashboard.",
    tags: ["AUTHOR", "ASSIGN", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/admin-paths",
    minRole: "admin",
    group: "mentorship",
  },
  {
    label: "ADMIN · ACADEMY",
    name: "Lesson Videos",
    description:
      "Write a lesson's text, publish it, then let AI draft a teacher script the pixel-sprite instructor narrates (Google TTS). Draft, edit, preview, approve.",
    tags: ["TEXT + VIDEO", "AI-DRAFTED", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/admin-lesson-videos",
    minRole: "admin",
    group: "mentorship",
  },
  {
    label: "LEADS",
    name: "Leads",
    description:
      "Companies hiring DevOps engineers right now. Paste a contact email, we draft the outreach.",
    tags: ["DAILY-REFRESH", "DEVOPS ROLES", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/sales-leads",
    minRole: "staff",
    requiredTag: "sales",
  },
  {
    label: "LEADS · OUTREACH",
    name: "Outreach",
    description:
      "Every email before it goes out — cold, follow-up, reply. Read it, fix the tone, hit send.",
    tags: ["HUMAN-GATED", "EDITABLE", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/sales",
    minRole: "staff",
    requiredTag: "sales",
  },
  {
    label: "LEADS · OVERVIEW",
    name: "Leads Dashboard",
    description:
      "Funnel counters, lead drawer, campaign view, suppression list, and outreach in one place.",
    tags: ["FUNNEL", "5 TABS", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/sales-dashboard",
    minRole: "staff",
    requiredTag: "sales",
  },
];

const SectionStyled = styled.section`
  padding: 128px 0 64px;
  background: var(--nd-black);
`;

const Title = styled.h1`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 56px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 16px;
  @media (max-width: 768px) { font-size: 36px; }
`;

const Lede = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 520px;
  margin: 0 0 48px;
`;

const GroupLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 48px 0 16px;
`;

const ToolCard = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);

  &:hover { border-color: var(--nd-border-visible); }
`;

const ToolCardLink = styled(Link)`
  display: block;
  height: 100%;
  text-decoration: none !important;
  color: inherit !important;

  &:hover > div { border-color: var(--nd-text-display) !important; }
`;

const ToolLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const ToolName = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 22px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 12px 0 8px;
`;

const ToolDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0 0 20px;
  flex-grow: 1;
`;

const ToolMeta = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const MetaTag = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  border: 1px solid var(--nd-border-visible);
  border-radius: 4px;
  padding: 4px 10px;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ color }) => color || "var(--nd-success)"};
  margin-right: 8px;
  vertical-align: middle;
`;

const StatusLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const ToolHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const renderCard = (tool, key) => {
  const body = (
    <ToolCard>
      <ToolHeader>
        <ToolLabel>{tool.label}</ToolLabel>
        <div>
          <StatusDot color={tool.statusColor} />
          <StatusLabel>{tool.status}</StatusLabel>
        </div>
      </ToolHeader>
      <ToolName>{tool.name}</ToolName>
      <ToolDesc>{tool.description}</ToolDesc>
      <ToolMeta>
        {tool.tags.map((tag, i) => (
          <MetaTag key={i}>{tag}</MetaTag>
        ))}
      </ToolMeta>
    </ToolCard>
  );
  return (
    <Col md="6" lg="4" key={key}>
      {tool.href ? <ToolCardLink to={tool.href}>{body}</ToolCardLink> : body}
    </Col>
  );
};

const ToolsPage = () => {
  const { role, tags } = useAuth();
  const currentRole = role || "public";
  const currentTags = tags || [];

  const tagOk = (t) => !t.requiredTag
    || currentRole === "admin"
    || hasTag(currentTags, t.requiredTag);

  const publicTools = TOOLS.filter((t) => t.minRole === "public");
  const clientTools = TOOLS.filter(
    (t) => t.minRole === "client" && hasRole(currentRole, "client"),
  );
  const staffTools = TOOLS.filter(
    (t) => t.minRole === "staff" && !t.requiredTag && hasRole(currentRole, "staff"),
  );
  const salesTools = TOOLS.filter(
    (t) => t.requiredTag === "sales" && tagOk(t) && hasRole(currentRole, "staff"),
  );
  const adminTools = TOOLS.filter(
    (t) => t.minRole === "admin" && !t.group && hasRole(currentRole, "admin"),
  );
  const mentorshipTools = TOOLS.filter(
    (t) => t.minRole === "admin" && t.group === "mentorship" && hasRole(currentRole, "admin"),
  );

  return (
    <>
      <Seo
        title="DevOps Tools"
        description="The toolbelt we ship with: Terraform, Pulumi, Karpenter, ArgoCD, Prometheus, Grafana, Datadog, Vault."
        pathname="/tools"
      />
      <PageWrapper>
      <SectionStyled>
        <Container>
          <Title>Tools</Title>
          <Lede>
            Internal tools we built for our own clients. Some are public,
            some unlock when you sign in with the right role.
          </Lede>

          <GroupLabel>Public</GroupLabel>
          <Row>{publicTools.map((t, i) => renderCard(t, `pub-${i}`))}</Row>

          {clientTools.length > 0 && (
            <>
              <GroupLabel>Client · Visibility</GroupLabel>
              <Row>{clientTools.map((t, i) => renderCard(t, `client-${i}`))}</Row>
            </>
          )}

          {staffTools.length > 0 && (
            <>
              <GroupLabel>Staff · Juniors & Engineers</GroupLabel>
              <Row>{staffTools.map((t, i) => renderCard(t, `staff-${i}`))}</Row>
            </>
          )}

          {salesTools.length > 0 && (
            <>
              <GroupLabel>Sales · Pipeline & Outreach</GroupLabel>
              <Row>{salesTools.map((t, i) => renderCard(t, `sales-${i}`))}</Row>
            </>
          )}

          {adminTools.length > 0 && (
            <>
              <GroupLabel>Admin · Operators</GroupLabel>
              <Row>{adminTools.map((t, i) => renderCard(t, `admin-${i}`))}</Row>
            </>
          )}

          {mentorshipTools.length > 0 && (
            <>
              <GroupLabel>Admin · Mentorship & Academy</GroupLabel>
              <Row>{mentorshipTools.map((t, i) => renderCard(t, `mentorship-${i}`))}</Row>
            </>
          )}
        </Container>
      </SectionStyled>
      <CTA />
      </PageWrapper>
    </>
  );
};

export default ToolsPage;
